import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Flame } from '@sorare/core/src/atoms/icons/Flame';
import { HeartIcon } from '@sorare/core/src/atoms/icons/HeartIcon';
import { iconPlus } from '@sorare/core/src/atoms/icons/system/iconPlus.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyL,
  BodyM,
  BrandM,
  BrandS,
} from '@sorare/core/src/atoms/typography';
import { Trapezoid } from '@sorare/core/src/atoms/ui/Trapezoid';
import { Dialog } from '@sorare/core/src/components/dialog';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { fantasy } from '@sorare/core/src/lib/glossary';

const Content = styled(Vertical)`
  padding: var(--triple-unit) var(--double-unit);
`;

const HeartsWithPlus = styled.div`
  position: relative;
`;

const PlusIcon = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: absolute;
  inset: 0;
  align-self: center;
`;
type Props = {
  open: boolean;
  onClose: () => void;
};

export const HeartsDialog = ({ open, onClose }: Props) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs">
      <Content gap={2} center>
        <Vertical gap={5} center>
          <Vertical gap={2} center>
            <Trapezoid
              style={
                {
                  '--background': 'var(--c-gradient-hotstreak)',
                } as CSSProperties
              }
              as={Horizontal}
            >
              <Flame color="var(--c-black)" />
              <BrandM color="var(--c-black)">
                <FormattedMessage {...fantasy.hotStreak} />
              </BrandM>
            </Trapezoid>
            <BodyL className="text-center">
              <FormattedMessage
                defaultMessage="Lose 1 heart for missing the target.{br}Losing all your hearts will reset your progress."
                values={{
                  br: <br />,
                }}
              />
            </BodyL>
          </Vertical>
          <Vertical gap={1} center>
            <Horizontal gap={2}>
              <HeartIcon status="active" size={5} />
              <HeartIcon status="active" size={5} />
              <HeartIcon status="active" size={5} />
              <HeartIcon size={5} />
            </Horizontal>
            <BrandS
              color="var(--c-rewards-orange)"
              style={{ alignSelf: 'flex-end' }}
            >
              <FormattedMessage defaultMessage="Miss" />
            </BrandS>
          </Vertical>

          <Vertical gap={1} center>
            <Horizontal gap={2}>
              <HeartIcon size={5} />
              <HeartIcon size={5} />
              <HeartIcon size={5} />
              <HeartIcon size={5} />
            </Horizontal>
            <BrandS
              color="var(--c-rewards-orange)"
              style={{ alignSelf: 'flex-start' }}
            >
              <FormattedMessage defaultMessage="Back to step 1" />
            </BrandS>
          </Vertical>

          <Vertical gap={1} center>
            <Horizontal gap={2}>
              <HeartIcon status="active" size={5} />
              <HeartIcon status="active" size={5} />
              <HeartIcon status="active" size={5} />
              <HeartsWithPlus>
                <HeartIcon size={5} />
                <PlusIcon>
                  <FontAwesomeIcon
                    icon={iconPlus}
                    color={getRarityColor(Rarity.rare)}
                  />
                </PlusIcon>
              </HeartsWithPlus>
            </Horizontal>
            <BodyM
              color="var(--c-nd-600)"
              className="text-gradient-color text-center"
              style={
                {
                  '--text-gradient-color':
                    'radial-gradient(57.19% 57.19% at 48.74% 67.6%, #FEF314 0%, #FF4E22 100%)',
                } as CSSProperties
              }
            >
              <FormattedMessage defaultMessage="Gain all lost hearts back when you advance your streak." />
            </BodyM>
          </Vertical>
          <Button size="large" color="primary" onClick={onClose}>
            <FormattedMessage defaultMessage="Confirm" />
          </Button>
        </Vertical>
      </Content>
    </Dialog>
  );
};
